import Accordion from '@/components/Accordion/Accordion';
import Button from '@/components/Button/Button';
import LoadingSpinner from '@/components/Button/LoadingSpinner/LoadingSpinner';
import Card from '@/components/Card/Card';
import Icon from '@/components/Icons/Icon';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useExistsActiveFreezes } from '@/services/freeze/freeze.service';
import { useStoreBundleGroups } from '@/services/groups/groups.service';
import { UserRoles } from '@/types/auth.types';
import { Group } from '@/types/groups.types';
import useConfirmationModal from '@/utils/useConfirmationModal/useConfirmationModal';
import useLocalUser from '@/utils/useLocalUser/useLocalUser';
import classNames from 'classnames';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from '../MyStores.module.css';
import CreateStoreGroups from './CreateStoreGroups/CreateStoreGroups';
import EditStoreGroupName from './EditStoreGroupName/EditStoreGroupName';
import PartialSuccessModal from './PartialSuccessModal/PartialSuccessModal';
import StoreGroupTable from './StoreGroupTable';
import { StoreGroupsProps } from './StoreGroups.types';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';

const StoreGroups: FC<StoreGroupsProps> = ({ stores, storesIsLoading }) => {
  const { t } = useTranslation();
  const { user } = useLocalUser();
  const isViewOnly = user?.role === UserRoles.VIEW;
  const { data, isLoading, isError, refetch } = useStoreBundleGroups(true);
  const { data: activeFreezeExists, isLoading: freezeIsLoading, isError: freezeIsError } = useExistsActiveFreezes();
  const [showCreateWizard, setShowCreateWizard] = useState(false);
  const [storeGroupToEdit, setStoreGroupToEdit] = useState<Group | null>(null);
  const storeGroups = data?.data || [];
  const handleClose = useCallback(() => setShowCreateWizard(false), [setShowCreateWizard]);
  const [confirmationModalIsOpen, setConfirmationModalIsOpen] = useState(false);
  const [confirmationModalTitle, setConfirmationModalTitle] = useState('');
  const { open } = useConfirmationModal();
  const openEditModal = (group: Group) => {
    setStoreGroupToEdit(group);
  };
  const closeEditModal = () => {
    setStoreGroupToEdit(null);
  };

  const [partialSuccessData, setPartialSuccessData] = useState<{
    storesWithErrors: { status: string; errorMessage: string; storeNo: string }[];
    storesSubmittedCount: number;
  }>({
    storesWithErrors: [],
    storesSubmittedCount: 0,
  });
  const closePartialSuccessModal = () => {
    setPartialSuccessData({
      storesWithErrors: [],
      storesSubmittedCount: 0,
    });
  };

  if (isError) {
    showToast({ toastType: ToastTypes.Error, id: 'storeGroupsError', label: t('error.fetchingStoreGroups') });
  }

  if (isError || freezeIsError) {
    showToast({
      toastType: ToastTypes.Error,
      id: 'freezeError',
      label: t('error.fetchingFreezeAndNationalProgramData'),
    });
  }

  const editAccess = !activeFreezeExists && !isViewOnly;

  if (isLoading || freezeIsLoading) return <LoadingSpinner />;
  return (
    <>
      {activeFreezeExists && (
        <Card className={styles.tooltipCard}>
          <Icon iconName="Info" />
          <div>{t('storeGroups.freezeNotification')}</div>
        </Card>
      )}
      <div className={styles.storeGroupsPage}>
        <div data-testid="StoreGroups" className={styles.storeGroups}>
          {storeGroups?.map((group) => (
            <Accordion
              key={group.id}
              title={
                <div className={classNames(styles.accordionTitle, { [styles.viewOnly]: isViewOnly })}>
                  <div>{group.name}</div>
                  {editAccess && <Icon iconName="Edit2" onClick={() => openEditModal(group)} />}
                  <div className={styles.storesCount}>
                    {group.storesCount} {t('storeGroups.storeLabel', { count: group.storesCount })}
                  </div>
                </div>
              }
            >
              <StoreGroupTable
                groupId={group.id}
                groupName={group.name}
                allGroups={storeGroups}
                editAccess={editAccess}
                globalFreezeExists={activeFreezeExists}
                setPartialSuccessData={setPartialSuccessData}
                confirmationModalIsOpen
                setConfirmationModalIsOpen={(param: boolean) => setConfirmationModalIsOpen(param)}
                setConfirmationModalIsTitle={(paramTitle: string) => setConfirmationModalTitle(paramTitle)}
              />
            </Accordion>
          ))}
        </div>
        {editAccess && !activeFreezeExists && storeGroups.length > 0 && (
          <div className={styles.createNewGroup}>
            <Button type="button" onClick={() => setShowCreateWizard(true)}>
              {t('storeGroups.newGroupButton')}
            </Button>
          </div>
        )}
        <CreateStoreGroups
          storeGroups={storeGroups}
          allStores={stores}
          storesIsLoading={storesIsLoading}
          isOpen={showCreateWizard}
          key={showCreateWizard.toString()}
          handleClose={handleClose}
          setConfirmationModalVisible={open}
          refetch={refetch}
          setPartialSuccessData={setPartialSuccessData}
        />
        <EditStoreGroupName
          isOpen={storeGroupToEdit !== null}
          onClose={closeEditModal}
          storeGroupToEdit={storeGroupToEdit}
          storeGroups={storeGroups}
        />
        <PartialSuccessModal
          isOpen={partialSuccessData.storesSubmittedCount > 0}
          onClose={closePartialSuccessModal}
          storesWithErrors={partialSuccessData.storesWithErrors}
          storesSubmittedCount={partialSuccessData.storesSubmittedCount}
        />

        <ConfirmationModal
          title={confirmationModalTitle}
          isOpen={confirmationModalIsOpen}
          onClose={() => setConfirmationModalIsOpen(false)}
        />
      </div>
    </>
  );
};

export default StoreGroups;
